import {
    Address,
    PatientBadge,
    ProviderInstitutionalClaimTemplate,
    ProviderPayerSubscriber,
    ProviderSubscriberPatient,
    Spinner,
} from '@enter/core';
import { Fade } from '@mui/material';
import { mdiAccount, mdiCakeVariant, mdiHomeCity, mdiWeightPound } from '@mdi/js';
import dateFns from 'date-fns';
import React from 'react';
import * as S from './patient-overview-card.styles';

interface IPatientOverviewCardProps {
    loading: boolean;
    subscriberPatient?: ProviderSubscriberPatient;
    payerSubscriber?: ProviderPayerSubscriber;
    institutionalTemplate?: ProviderInstitutionalClaimTemplate;
}

export class PatientOverviewCard extends React.Component<IPatientOverviewCardProps> {
    public render(): React.ReactElement {
        const { loading, subscriberPatient } = this.props;
        return (
            <S.PatientOverviewCard size="large">
                <S.TopRow>
                    <S.Title>Patient Information</S.Title>
                    {subscriberPatient && subscriberPatient.patient && this.renderBadge()}
                </S.TopRow>
                {loading && this.renderLoader()}
                {!loading && this.renderPatient()}
            </S.PatientOverviewCard>
        );
    }
    private renderLoader(): React.ReactElement {
        return (
            <Fade appear={true} in={true}>
                <S.Loading>
                    <Spinner />
                </S.Loading>
            </Fade>
        );
    }
    private renderBadge(): React.ReactElement | undefined {
        const { subscriberPatient } = this.props;
        if (subscriberPatient && subscriberPatient.patient) {
            const { patient } = subscriberPatient;
            return (
                <PatientBadge
                    id={patient.id}
                    first={patient.first}
                    last={patient.last}
                    suffix={patient.suffix}
                    tooltip="View claims for Patient"
                    path={`/claims/by-patient/${patient.id}`}
                />
            );
        }
    }
    private renderPatient(): React.ReactElement | undefined {
        const { institutionalTemplate, subscriberPatient, payerSubscriber } = this.props;
        if (subscriberPatient && subscriberPatient.patient) {
            const {
                patient: { id, first, last, dob, sex, weight, addresses },
            } = subscriberPatient;
            return (
                <>
                    {institutionalTemplate && (
                        <S.RecordRow>
                            <S.RecordLabel>Patient Control #</S.RecordLabel>{' '}
                            <S.RecordNumber>{institutionalTemplate.f03aPcn}</S.RecordNumber>
                            <S.RecordLabel>·</S.RecordLabel>
                            <S.RecordLabel>Medical Record No.</S.RecordLabel>{' '}
                            <S.RecordNumber>{institutionalTemplate.f03bMrn}</S.RecordNumber>
                        </S.RecordRow>
                    )}
                    <S.PatientRow>
                        <S.PatientName>
                            {first} {last}
                        </S.PatientName>
                        <S.PatientDivider>/</S.PatientDivider>
                        {payerSubscriber && (
                            <>
                                <S.MemberLabel>Member ID</S.MemberLabel>
                                <S.MemberId>{payerSubscriber.memberId}</S.MemberId>
                            </>
                        )}
                    </S.PatientRow>
                    <S.IconRow>
                        <S.DobIcon path={mdiCakeVariant} />
                        {dateFns.format(dob, 'MM/DD/YYYY')}
                        <S.PatientIcon path={mdiAccount} />
                        {sex}
                        <S.WeightIcon path={mdiWeightPound} />
                        {weight}
                    </S.IconRow>
                    <S.IconRow>{addresses && this.renderAddresses(addresses)}</S.IconRow>
                    {/* FIXME: suppress Patient Details link until page is available */}
                    {false && (
                        <S.FooterRow>
                            <S.FooterLink to={`/patients/${id}`}>View patient details</S.FooterLink>
                        </S.FooterRow>
                    )}
                </>
            );
        }
    }
    private renderAddresses(addresses: Address[]): React.ReactElement {
        const address = addresses[0];
        const homeCity = `${address.city}, ${address.state} ${address.postal}`;
        return (
            <>
                <S.AddressIcon path={mdiHomeCity} />
                {homeCity}
            </>
        );
    }
}
